import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import { buildDayFlightQueryParams } from "../utils";
import { EMPTY_FILTERS } from "../constants";
import { Page, Filters, FlightsList, WidePageContainer } from "../components";
import { CockpitCard, PageHeader } from "../components/design";
import {
  ListActiveFilters,
  ListCommandSearch,
  ListFilterPicker,
  ListQuickFilters,
} from "../components/listing";
import { FlightSearchSummary } from "../components/flights/FlightSearchSummary";
import {
  buildListQueryKey,
  countActiveFlightFilters,
  hasManagedFlightListParameters,
  hydrateFlightFilters,
  parseFlightFiltersFromUrl,
  serializeFlightFiltersForSavedView,
  serializeFlightFiltersToUrl,
  stableListStateFingerprint,
  toFlightListQueryState,
  useListKeyboardController,
} from "../listing";
import {
  useDeleteFlightFilter,
  useInfiniteFlights,
  useSavedFlightFilters,
  useSaveFlightFilter,
  useUpdateFlightFilter,
} from "../hooks/useFlights";
import useAuthSnapshot from "../hooks/useAuthSnapshot";
import { useOperators as useOperatorOptions } from "../hooks/useUsers";
import { API_URL } from "../config";

const dateLabelFormatter = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  timeZone: "UTC",
});

const toDateLabel = (value?: Dayjs | null) => {
  if (!value) {
    return "";
  }

  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value as any);
  if (Number.isNaN(date.valueOf())) {
    return "";
  }

  return dateLabelFormatter.format(date);
};

const Flights: FunctionComponent = () => {
  const { isSuperAdmin, isAcmiClient, operatorRef, userData } = useAuthSnapshot();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() =>
    parseFlightFiltersFromUrl(searchParams, isSuperAdmin),
  );
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveAsPinned, setSaveAsPinned] = useState(true);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [saveChoiceDialogOpen, setSaveChoiceDialogOpen] = useState(false);
  const [activeSavedFilterId, setActiveSavedFilterId] = useState<number | null>(null);
  const [savedFiltersAnchor, setSavedFiltersAnchor] = useState<HTMLElement | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const hasAppliedDefaultRef = useRef(false);
  const lastWrittenParamsRef = useRef<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const operatorOptionsQuery = useOperatorOptions(isSuperAdmin);
  const savedFiltersQuery = useSavedFlightFilters();
  const saveFilterMutation = useSaveFlightFilter();
  const updateFilterMutation = useUpdateFlightFilter();
  const deleteFilterMutation = useDeleteFlightFilter();

  const listQueryState = useMemo(() => toFlightListQueryState(filters), [filters]);
  const queryParams = useMemo(() => buildDayFlightQueryParams(filters), [filters]);
  const exportUrl = useMemo(() => {
    const params = new URLSearchParams(queryParams.replace(/^\?/, ""));
    ["paginationMode", "dayLimit", "beforeDay", "limit", "offset"].forEach((key) => params.delete(key));
    return `${API_URL}/flights/export.csv?${params.toString()}`;
  }, [queryParams]);
  const queryKey = useMemo(
    () => buildListQueryKey(
      "flights",
      (isSuperAdmin ? filters.operatorRef : operatorRef) ?? null,
      listQueryState,
    ),
    [filters.operatorRef, isSuperAdmin, listQueryState, operatorRef],
  );
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    isError,
  } = useInfiniteFlights(queryParams, queryKey);
  const savedFilters = savedFiltersQuery.data?.filters || [];
  const pinnedFilters = savedFilters.filter((filter) => filter.isPinned);
  const operatorOptions = operatorOptionsQuery.data || [];
  const activeFilterCount = countActiveFlightFilters(filters, isSuperAdmin);
  const hasAdvancedFilter = Boolean(
    filters.date || filters.dateFrom || filters.dateTo || filters.callsign ||
    filters.registration || filters.departureAirport || filters.arrivalAirport ||
    filters.flightID || filters.commercialFlightNumber || filters.tlc || filters.atcType || filters.flightPurpose || filters.purposeReview ||
    (isSuperAdmin && filters.operatorRef),
  );
  const flightPages = data?.pages || [];
  const firstFlightPage = flightPages[0];
  const flights = useMemo(() => {
    const byId = new Map<string, Flight>();
    flightPages.forEach((pageData) => {
      (pageData?.flights || []).forEach((flight: Flight) => {
        byId.set(flight.flightID, flight);
      });
    });
    return Array.from(byId.values());
  }, [flightPages]);

  useEffect(() => {
    if (hasAdvancedFilter) {
      setShowAdvancedFilters(true);
    }
  }, [hasAdvancedFilter]);

  const updateFilters = (nextFilters: Filters) => {
    setFilters(nextFilters);
    const nextParams = serializeFlightFiltersToUrl(
      nextFilters,
      isSuperAdmin,
      searchParams,
    );
    const nextParamsString = nextParams.toString();
    if (nextParamsString !== searchParams.toString()) {
      lastWrittenParamsRef.current = nextParamsString;
      setSearchParams(nextParams, { replace: true });
    }
  };

  const currentParamsString = searchParams.toString();

  useListKeyboardController({
    searchInputRef,
    rowSelector: "[data-flight-list-row='true']",
    onEscape: () => {
      setSavedFiltersAnchor(null);
      setSaveChoiceDialogOpen(false);
      setSaveDialogOpen(false);
    },
  });

  useEffect(() => {
    if (lastWrittenParamsRef.current === currentParamsString) {
      lastWrittenParamsRef.current = null;
      return;
    }

    const urlFilters = parseFlightFiltersFromUrl(searchParams, isSuperAdmin);
    const urlFingerprint = stableListStateFingerprint(toFlightListQueryState(urlFilters));
    setFilters((currentFilters) => (
      stableListStateFingerprint(toFlightListQueryState(currentFilters)) === urlFingerprint
        ? currentFilters
        : urlFilters
    ));
  }, [currentParamsString, isSuperAdmin, searchParams]);

  useEffect(() => {
    if (hasAppliedDefaultRef.current) {
      return;
    }

    if (hasManagedFlightListParameters(searchParams)) {
      hasAppliedDefaultRef.current = true;
      return;
    }

    if (!savedFiltersQuery.isSuccess) {
      return;
    }

    const defaultFilter = savedFilters.find((filter) => filter.isDefault);
    if (!defaultFilter) {
      hasAppliedDefaultRef.current = true;
      return;
    }

    hasAppliedDefaultRef.current = true;
    updateFilters(hydrateFlightFilters(defaultFilter.filters, isSuperAdmin));
    setActiveSavedFilterId(defaultFilter.id);
  }, [
    isSuperAdmin,
    savedFilters,
    savedFiltersQuery.isSuccess,
    searchParams,
    setSearchParams,
  ]);

  const mergeFilters = (patch: Partial<Filters>) => {
    updateFilters({ ...filters, ...patch });
  };

  const handleDateChange = (date: Dayjs | null) => {
    updateFilters({ ...filters, date, dateFrom: null, dateTo: null });
  };

  const handleSearchChange = (search: string) => {
    mergeFilters({ search });
  };

  const handleCallsignChange = (_: any, callsign: string | null) => {
    mergeFilters({ callsign });
  };

  const handleRegistrationChange = (_: any, registration: string | null) => {
    mergeFilters({ registration });
  };

  const handleDepartureAirportChange = (
    _: any,
    departureAirport: string | null,
  ) => {
    mergeFilters({ departureAirport });
  };

  const handleArrivalAirportChange = (
    _: any,
    arrivalAirport: string | null,
  ) => {
    mergeFilters({ arrivalAirport });
  };

  const handleFlightIDChange = (
    _: any,
    flightID: string | null,
  ) => {
    mergeFilters({ flightID });
  };

  const handleCommercialFlightNumberChange = (
    _: any,
    commercialFlightNumber: string | null,
  ) => {
    mergeFilters({ commercialFlightNumber });
  };

  const handleTLCChange = (
    _: any,
    tlc: string | null,
  ) => {
    mergeFilters({ tlc });
  };

  const handleOperatorChange = (_: any, operator: Operator | null) => {
    mergeFilters({ operatorRef: operator?.id || null });
  };

  const applyQuickFilter = (type: "today" | "yesterday" | "last7" | "myTlc") => {
    if (type === "today") {
      updateFilters({ ...filters, date: dayjs(), dateFrom: null, dateTo: null });
      return;
    }

    if (type === "yesterday") {
      updateFilters({
        ...filters,
        date: dayjs().subtract(1, "day"),
        dateFrom: null,
        dateTo: null,
      });
      return;
    }

    if (type === "last7") {
      updateFilters({
        ...filters,
        date: null,
        dateFrom: dayjs().subtract(6, "day"),
        dateTo: dayjs(),
      });
      return;
    }

    const userTlc = `${userData?.tlc || ""}`.trim();
    if (userTlc) {
      mergeFilters({ tlc: userTlc });
    }
  };

  const clearFilter = (key: keyof Filters) => {
    mergeFilters({ [key]: key === "operatorRef" ? null : "" } as Partial<Filters>);
  };

  const clearDateRange = () => {
    mergeFilters({ dateFrom: null, dateTo: null });
  };

  const clearAllFilters = () => {
    updateFilters(EMPTY_FILTERS);
    setActiveSavedFilterId(null);
  };

  const handleApplySavedFilter = (savedFilter: SavedFlightFilter) => {
    updateFilters(hydrateFlightFilters(savedFilter.filters, isSuperAdmin));
    setActiveSavedFilterId(savedFilter.id);
    setSavedFiltersAnchor(null);
  };

  const activeSavedFilter = savedFilters.find((filter) => filter.id === activeSavedFilterId) || null;

  const openCreateFilterDialog = (initialName = "") => {
    setSaveChoiceDialogOpen(false);
    setSaveName(initialName);
    setSaveAsPinned(true);
    setSaveAsDefault(false);
    setSaveDialogOpen(true);
  };

  const handleOpenSaveFlow = () => {
    if (activeSavedFilter) {
      setSaveChoiceDialogOpen(true);
      return;
    }

    openCreateFilterDialog();
  };

  const handleUpdateCurrentFilter = () => {
    if (!activeSavedFilter) {
      setSaveChoiceDialogOpen(false);
      return;
    }

    updateFilterMutation.mutate(
      {
        id: activeSavedFilter.id,
        name: activeSavedFilter.name,
        filters: serializeFlightFiltersForSavedView(filters, isSuperAdmin),
        isPinned: activeSavedFilter.isPinned,
        isDefault: activeSavedFilter.isDefault,
      },
      {
        onSuccess: () => {
          setSaveChoiceDialogOpen(false);
        },
      },
    );
  };

  const handleSaveCurrentFilter = () => {
    const name = saveName.trim();
    if (!name) {
      return;
    }

    saveFilterMutation.mutate(
      {
        name,
        filters: serializeFlightFiltersForSavedView(filters, isSuperAdmin),
        isPinned: saveAsPinned,
        isDefault: saveAsDefault,
      },
      {
        onSuccess: (savedFilter) => {
          setSaveDialogOpen(false);
          setActiveSavedFilterId(savedFilter?.id || null);
          setSaveName("");
          setSaveAsPinned(true);
          setSaveAsDefault(false);
        },
      },
    );
  };

  const handleTogglePinned = (savedFilter: SavedFlightFilter) => {
    updateFilterMutation.mutate({
      id: savedFilter.id,
      name: savedFilter.name,
      filters: savedFilter.filters,
      isDefault: savedFilter.isDefault,
      isPinned: !savedFilter.isPinned,
    });
  };

  const handleDeleteSavedFilter = (id: number) => {
    if (activeSavedFilterId === id) {
      setActiveSavedFilterId(null);
    }
    deleteFilterMutation.mutate(id);
  };

  const selectedOperatorName = useMemo(() => {
    if (!filters.operatorRef) {
      return null;
    }

    const operator = operatorOptions.find((entry: Operator) => Number(entry.id) === Number(filters.operatorRef));
    return operator?.name || operator?.company || `Operator ${filters.operatorRef}`;
  }, [filters.operatorRef, operatorOptions]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "1600px 0px" },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const activeChips = [
    filters.search ? { key: "search", label: `Search: ${filters.search}`, onDelete: () => clearFilter("search") } : null,
    filters.date ? { key: "date", label: `Date: ${toDateLabel(filters.date)}`, onDelete: () => mergeFilters({ date: null }) } : null,
    filters.dateFrom || filters.dateTo ? {
      key: "dateRange",
      label: `Range: ${toDateLabel(filters.dateFrom)} - ${toDateLabel(filters.dateTo)}`,
      onDelete: clearDateRange,
    } : null,
    filters.callsign ? { key: "callsign", label: `Callsign: ${filters.callsign}`, onDelete: () => clearFilter("callsign") } : null,
    filters.registration ? { key: "registration", label: `Registration: ${filters.registration}`, onDelete: () => clearFilter("registration") } : null,
    filters.departureAirport ? { key: "departure", label: `Departure: ${filters.departureAirport}`, onDelete: () => clearFilter("departureAirport") } : null,
    filters.arrivalAirport ? { key: "arrival", label: `Arrival: ${filters.arrivalAirport}`, onDelete: () => clearFilter("arrivalAirport") } : null,
    filters.flightID ? { key: "flightID", label: `Flight ID: ${filters.flightID}`, onDelete: () => clearFilter("flightID") } : null,
    filters.commercialFlightNumber ? {
      key: "commercialFlightNumber",
      label: `Commercial: ${filters.commercialFlightNumber}`,
      onDelete: () => clearFilter("commercialFlightNumber"),
    } : null,
    ...(['atcType','flightPurpose','purposeReview'] as const).filter(key=>filters[key]).map(key=>({key,label:`${key==='atcType'?'Flight type':key==='flightPurpose'?'Flight purpose':'Purpose review'}: ${filters[key]}`,onDelete:()=>clearFilter(key)})),
    filters.tlc ? { key: "tlc", label: `TLC: ${filters.tlc}`, onDelete: () => clearFilter("tlc") } : null,
    isSuperAdmin && filters.operatorRef ? {
      key: "operator",
      label: `Operator: ${selectedOperatorName}`,
      onDelete: () => clearFilter("operatorRef"),
    } : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onDelete: () => void }>;

  return (
    <WidePageContainer sx={{ py: { xs: 2, md: 3 }, minWidth: 0, overflowX: "hidden" }}>
      <Page style={{ paddingTop: 0 }}>
        <Backdrop sx={{ zIndex: 1 }} open={isLoading}>
          <CircularProgress />
        </Backdrop>
        <Stack spacing={2.5} className="cockpit-ui">
          <PageHeader
            eyebrow="Flight records"
            title="Flights"
            description="Search every flight record, reopen operational workflows and export the exact filtered result."
            metadata={
              <Typography variant="caption" sx={{ color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
                {isSuperAdmin && selectedOperatorName ? `OPERATOR · ${selectedOperatorName}` : userData?.operator_name ? `OPERATOR · ${userData.operator_name}` : "ALL TIMES ZULU"}
              </Typography>
            }
          />

          <CockpitCard sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.5}>
              <ListCommandSearch
                value={filters.search || ""}
                onChange={handleSearchChange}
                inputRef={searchInputRef}
                label="Search flights"
                placeholder="Commercial number, callsign, EDDF LOWW, registration, TLC or flight ID"
                helperText="Press Ctrl/⌘ K from anywhere on the page"
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <ListFilterPicker
                    label="Add filter"
                    items={[
                      "Date", "Callsign", "Registration", "Departure airport", "Arrival airport", "Commercial number", "Flight ID", "TLC", "Flight type", "Flight purpose", "Purpose review",
                      ...(isSuperAdmin ? ["Operator"] : []),
                    ].map((label) => ({ key: label, label, active: showAdvancedFilters, onSelect: () => setShowAdvancedFilters(true) }))}
                  />
                  <Button startIcon={<TuneIcon />} variant="outlined" onClick={(event) => setSavedFiltersAnchor(event.currentTarget)}>
                    Saved filters
                  </Button>
                  <Button startIcon={<BookmarkAddOutlinedIcon />} variant="contained" onClick={handleOpenSaveFlow} disabled={activeFilterCount === 0}>
                    Save current
                  </Button>
                </Stack>
                <ListQuickFilters items={[
                  { key: "today", label: "Today", onSelect: () => applyQuickFilter("today") },
                  { key: "yesterday", label: "Yesterday", onSelect: () => applyQuickFilter("yesterday") },
                  { key: "last7", label: "Last 7 days", onSelect: () => applyQuickFilter("last7") },
                  ...(userData?.tlc ? [{ key: "myTlc", label: "My TLC", onSelect: () => applyQuickFilter("myTlc") }] : []),
                  ...pinnedFilters.map((savedFilter) => ({ key: `saved-${savedFilter.id}`, label: savedFilter.name, active: savedFilter.id === activeSavedFilterId, onSelect: () => handleApplySavedFilter(savedFilter) })),
                ]} />
              </Stack>
              {showAdvancedFilters ? (
                <Box sx={{ pt: 1, borderTop: "1px solid var(--line)" }}>
                  <Filters
                    options={firstFlightPage?.filters || {}}
                    filters={filters}
                    searchInputRef={searchInputRef}
                    hideSearch
                    isSuperAdmin={isSuperAdmin}
                    operatorOptions={operatorOptions}
                    onSearchChange={handleSearchChange}
                    onOperatorChange={handleOperatorChange}
                    onDateChange={handleDateChange}
                    onCallsignChange={handleCallsignChange}
                    onRegistrationChange={handleRegistrationChange}
                    onDepartureAirportChange={handleDepartureAirportChange}
                    onArrivalAirportChange={handleArrivalAirportChange}
                    onFlightIDChange={handleFlightIDChange}
                    onCommercialFlightNumberChange={handleCommercialFlightNumberChange}
                    onTLCChange={handleTLCChange}
                    onPurposeFilterChange={mergeFilters}
                    purposeOptions={firstFlightPage?.purposeOptions}
                  />
                  <Button size="small" onClick={() => setShowAdvancedFilters(false)} sx={{ mt: 1 }}>Close filter picker</Button>
                </Box>
              ) : null}
            </Stack>
          </CockpitCard>

          <ListActiveFilters
            items={activeChips.map((chip) => ({ key: chip.key, label: chip.label, onRemove: chip.onDelete }))}
            onClearAll={clearAllFilters}
          />

          {isError ? (
            <Alert severity="error" variant="outlined">
              Unable to load flights. Please try again later.
            </Alert>
          ) : null}

          <FlightSearchSummary
            totalCount={firstFlightPage?.totalCount ?? 0}
            summary={firstFlightPage?.summary || null}
            exportUrl={exportUrl}
          />

          <Stack spacing={2}>
            {isSuccess ? (
              <FlightsList
                items={flights}
                showOperator={isSuperAdmin && !filters.operatorRef}
                canDelete={!isAcmiClient}
              />
            ) : null}
            <Box ref={loadMoreRef} sx={{ minHeight: 1 }} />
            {isFetchingNextPage ? (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading more days
                </Typography>
              </Stack>
            ) : null}
            {hasNextPage && !isFetchingNextPage ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button variant="outlined" onClick={() => fetchNextPage()}>
                  Load more
                </Button>
              </Box>
            ) : null}
            {isSuccess && flights.length > 0 && !hasNextPage ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 1 }}>
                All matching days loaded
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        <Menu
          anchorEl={savedFiltersAnchor}
          open={Boolean(savedFiltersAnchor)}
          onClose={() => setSavedFiltersAnchor(null)}
        >
          {!savedFilters.length ? (
            <MenuItem disabled>No saved filters yet</MenuItem>
          ) : null}
          {savedFilters.map((savedFilter) => (
            <MenuItem key={savedFilter.id} sx={{ gap: 1, minWidth: 280 }}>
              <Box
                component="button"
                onClick={() => handleApplySavedFilter(savedFilter)}
                style={{
                  appearance: "none",
                  border: 0,
                  background: "transparent",
                  padding: 0,
                  flex: 1,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {savedFilter.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {savedFilter.isDefault ? "Default" : "Saved filter"}
                </Typography>
              </Box>
              <Tooltip title={savedFilter.isPinned ? "Unpin filter" : "Pin filter"}>
                <IconButton size="small" onClick={() => handleTogglePinned(savedFilter)}>
                  {savedFilter.isPinned ? <PushPinIcon fontSize="inherit" /> : <PushPinOutlinedIcon fontSize="inherit" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete saved filter">
                <IconButton size="small" color="error" onClick={() => handleDeleteSavedFilter(savedFilter.id)}>
                  <DeleteOutlineIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))}
        </Menu>

        <Dialog
          open={saveChoiceDialogOpen}
          onClose={() => setSaveChoiceDialogOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Save filter changes</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Update "{activeSavedFilter?.name}" with the current filters, or save this as a new filter?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveChoiceDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => openCreateFilterDialog()} variant="outlined">
              Create new filter
            </Button>
            <Button
              onClick={handleUpdateCurrentFilter}
              variant="contained"
              disabled={updateFilterMutation.isLoading}
            >
              Update current filter
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Save current filter</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Filter name"
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                autoFocus
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveAsPinned}
                    onChange={(event) => setSaveAsPinned(event.target.checked)}
                  />
                }
                label="Pin as quick filter"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveAsDefault}
                    onChange={(event) => setSaveAsDefault(event.target.checked)}
                  />
                }
                label="Use as default when opening Flights"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveCurrentFilter}
              variant="contained"
              disabled={!saveName.trim() || saveFilterMutation.isLoading}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Page>
    </WidePageContainer>
  );
};

export default Flights;
